import React from "react";
import { Breadcrumb, BreadcrumbItem, Button } from "reactstrap";
import CreateFolder from "../CreateFolder";
import AddImage from "../AddImage";
import FolderForm from "../FolderForm";
import ConfirmUploadImg from "../ConfirmUploadImg";
import Folder from "../Folder";
import type { MediaFolder } from "../Folder";
import Image from "../Image";
import type { MediaImage } from "../ModelMediaContent/ImageInformation";

export type MediaBrowserViewProps = {
  mode?: "manage" | "select";
  path?: string;
  folders?: MediaFolder[];
  pictures?: MediaImage[];
  showElemNumbers?: number;
  totalCount?: number;
  onSeeMore?: () => void;
  onHomeBack?: () => void;
  onStepBack?: () => void;
  onFolderClick?: (folder: MediaFolder) => void;
  onRefresh?: () => void;
  onImageClick?: (image: MediaImage) => void;
  // manage mode
  showFormFolder?: boolean;
  /** The local file picked for upload (its preview `secure_url` gates the confirm card). */
  img?: { secure_url?: string | string[] };
  hideLocalImg?: boolean;
  onShowForm?: () => void;
  onCloseForm?: () => void;
  onSubmitFolder?: (...args: unknown[]) => void;
  onImgToUpload?: (...args: unknown[]) => void;
  onUploadAction?: (...args: unknown[]) => void;
  onAltSaved?: (publicId: string, alt: string) => void;
};

/**
 * MediaBrowserView — the single presentational browser shared by the standalone
 * Media page and the modal image picker (SCRUM-904). Driven entirely by the
 * useMediaBrowser view-model. `mode` gates the manage-only surface (create folder,
 * upload, per-image info modal) vs the select surface (click an image to pick it).
 */
const MediaBrowserView = ({
  mode = "manage",
  path = "",
  folders = [],
  pictures = [],
  showElemNumbers = 10,
  totalCount = 0,
  onSeeMore,
  onHomeBack,
  onStepBack,
  onFolderClick,
  onRefresh,
  onImageClick,
  // manage mode
  showFormFolder = false,
  img = {},
  hideLocalImg = false,
  onShowForm,
  onCloseForm,
  onSubmitFolder,
  onImgToUpload,
  onUploadAction,
  onAltSaved,
}: MediaBrowserViewProps) => {
  const isManage = mode === "manage";
  const segments = (path || "").split("/").filter((s) => s !== "");
  const crumbs = segments.length ? segments : [""];

  return (
    <div className="container">
      {/* Breadcrumb */}
      <div className="row">
        <Breadcrumb
          tag="nav"
          listTag="div"
          listClassName="breadcrumb-links bg-white justify-content-left"
        >
          {crumbs.map((elem, idx) =>
            idx === crumbs.length - 1 ? (
              <BreadcrumbItem tag="h3" key={crumbs.slice(0, idx + 1).join("/")} active>
                <strong> Country code: {elem} </strong>
              </BreadcrumbItem>
            ) : (
              <BreadcrumbItem tag="h3" key={crumbs.slice(0, idx + 1).join("/")}>
                <strong> {elem} </strong>
              </BreadcrumbItem>
            )
          )}
        </Breadcrumb>
      </div>

      {/* Header row */}
      <div className="row justify-content-center" style={{ marginBottom: 80 }}>
        <div className="col-auto">
          <h2 className="mb-0">Folders ( {folders.length} )</h2>
        </div>
        <div className="col-auto">
          <h2 className="mb-0">Pictures ( {pictures.length} )</h2>
        </div>
        <div className="col-auto">
          <Button
            type="button"
            size="md"
            className="btn-icon"
            onClick={onRefresh}
            style={{ backgroundColor: "transparent" }}
          >
            <span className="btn-inner--icon">
              <i className="fas fa-sync-alt" /> Refresh
            </span>
          </Button>
        </div>

        {(path || "").length > 6 ? (
          <div className="col-auto justify-content-right" style={{ width: "100%" }}>
            <Button
              type="button"
              size="md"
              className="btn-icon"
              onClick={onHomeBack}
              style={{ backgroundColor: "transparent" }}
            >
              <span className="btn-inner--icon">
                <i className="fa fa-home" /> Home
              </span>
            </Button>

            <Button
              type="button"
              size="md"
              className="btn-icon"
              onClick={onStepBack}
              style={{ backgroundColor: "transparent" }}
            >
              <span className="btn-inner--icon">
                <i className="ni ni-bold-left" />
              </span>
              Back
            </Button>
          </div>
        ) : null}
      </div>

      {isManage && showFormFolder && (
        <FolderForm handleCloseForm={onCloseForm} handleSubmitForm={onSubmitFolder} />
      )}

      {isManage && img?.secure_url && (
        <ConfirmUploadImg ConfirmUpload={onUploadAction} img={img} path={path} />
      )}

      {/* Grid */}
      <div className="row">
        {isManage && <CreateFolder requestForm={onShowForm} />}
        {isManage && (
          <AddImage ImageUpload={onImgToUpload} hideLocalImg={hideLocalImg} />
        )}

        {folders.slice(0, showElemNumbers).map((folder) => (
          <Folder key={folder.path ?? folder.name} folder={folder} clickHandler={onFolderClick} />
        ))}

        {pictures.slice(0, showElemNumbers).map((image) =>
          isManage ? (
            <Image key={image.public_id} image={image} imageInformation onAltSaved={onAltSaved} />
          ) : (
            <Image
              key={image.public_id}
              image={image}
              clickHandler={onImageClick}
              modelCall={true}
              noInformation
            />
          )
        )}
      </div>

      {/* See more */}
      {showElemNumbers < totalCount ? (
        <div className="text-center" style={{ margin: 40 }}>
          <Button
            color="primary"
            type="button"
            size="lg"
            className="btn-icon justify-content-center"
            onClick={onSeeMore}
          >
            <span className="btn-inner--icon">
              <i className="ni ni-bold-down" />
            </span>
            See more ( {showElemNumbers} / {totalCount} )
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default MediaBrowserView;
